/** Money is integer minor units everywhere (docs/02 §6). */
export function formatMoney(amountMinor: number, currency = "AFN"): string {
  const major = Math.round(amountMinor / 100);
  const grouped = major.toLocaleString("en-US");
  return currency === "AFN" ? `${grouped} ؋` : `$${grouped}`;
}

const DARI_MONTHS = [
  "حمل", "ثور", "جوزا", "سرطان", "اسد", "سنبله",
  "میزان", "عقرب", "قوس", "جدی", "دلو", "حوت",
];

const G_DAYS = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];

export function gregorianToJalali(gy: number, gm: number, gd: number) {
  const gy2 = gm > 2 ? gy + 1 : gy;
  let days =
    355666 + 365 * gy + Math.floor((gy2 + 3) / 4) - Math.floor((gy2 + 99) / 100) +
    Math.floor((gy2 + 399) / 400) + gd + G_DAYS[gm - 1];
  let jy = -1595 + 33 * Math.floor(days / 12053);
  days %= 12053;
  jy += 4 * Math.floor(days / 1461);
  days %= 1461;
  if (days > 365) {
    jy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }
  const jm = days < 186 ? 1 + Math.floor(days / 31) : 7 + Math.floor((days - 186) / 30);
  const jd = 1 + (days < 186 ? days % 31 : (days - 186) % 30);
  return { jy, jm, jd };
}

/** "2026-08-21" → "۳۰ اسد ۱۴۰۵" (Jalali with Afghan month names). */
export function formatJalali(isoDate: string): string {
  const [gy, gm, gd] = isoDate.split("-").map(Number);
  const { jy, jm, jd } = gregorianToJalali(gy, gm, gd);
  return `${jd} ${DARI_MONTHS[jm - 1]} ${jy}`;
}

/**
 * Jalali date for an *instant* (a timestamp), resolved in Kabul time.
 *
 * Slicing an ISO string would read the UTC day, which is the previous day for
 * anything happening in Kabul before 04:30 — a contract issued at 02:00 would
 * be dated yesterday. Asia/Kabul is +04:30 and has no DST.
 */
export function formatJalaliInstant(date: Date): string {
  // en-CA renders as YYYY-MM-DD, which is what formatJalali parses.
  const isoDay = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kabul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
  return formatJalali(isoDay);
}

/** Firestore serverTimestamp serializes over REST as { _seconds }. */
type FsTimestamp = { _seconds: number };

/**
 * Same, for the two shapes the REST API hands back. Routed through
 * formatJalaliInstant so it resolves in Kabul rather than slicing the UTC day.
 */
export function formatJalaliTs(value: string | FsTimestamp | null | undefined): string {
  if (!value) return "—";
  const date = typeof value === "string" ? new Date(value) : new Date(value._seconds * 1000);
  return formatJalaliInstant(date);
}

export function localized(name: Record<string, string> | undefined, locale = "fa"): string {
  if (!name) return "";
  return name[locale] ?? name.fa ?? name.en ?? Object.values(name)[0] ?? "";
}
